/**
 * INT-0027 (scheduler cold start): the quick-availability wizard's pure core,
 * shared by the admin preview and the API so both count the same slots and
 * place the same games.
 *
 * - `buildQuickSlots` turns days-of-week + a time window + a date range into
 *   ordinary venue-availability TimeSlot docs (one per date, `availableDate`
 *   MM-DD-YYYY, "HH:mm" times) — downstream schedulers see no new concept.
 * - `planGamePlacement` gives every time-less game (the INT-0043 drafted
 *   schedule) a start time: a game keeps its date when that weekday is in
 *   the window, otherwise it moves to the next chosen day on or after it;
 *   games beyond a day's capacity spill forward; round order is preserved.
 */
import { formatMinutesForGameTime, parseTimeToMinutes } from './gameDateTime';

export interface QuickAvailabilityInput {
  /** 0 = Sunday … 6 = Saturday */
  daysOfWeek: number[];
  /** "HH:mm" 24-hour */
  startTime: string;
  /** "HH:mm" 24-hour, after startTime */
  endTime: string;
  gameDurationInMinutes: number;
  /** YYYY-MM-DD, inclusive */
  dateFrom: string;
  /** YYYY-MM-DD, inclusive */
  dateTo: string;
}

export interface QuickSlot {
  dateKey: string;
  availableDate: string;
  startTime: string;
  endTime: string;
  gameDurationInMinutes: number;
  maxGames: number;
}

export interface PlaceableGame {
  id: string;
  /** YYYY-MM-DD the game currently sits on ('' when unknown) */
  dateKey: string;
  round?: number | null;
  gameNumber?: number | null;
}

export interface GamePlacement {
  id: string;
  dateKey: string;
  availableDate: string;
  startMinutes: number;
  time: string;
  /** Which parallel lane (court/field) took the game: `laneId` of the slot. */
  laneId: string;
}

/**
 * One bookable window on one lane (a court, a field, a venue without
 * sub-venues) on one date. The wizard's "3 courts" become 3 lanes with the
 * same windows (INT-0052); hand-made TimeSlots map 1:1 (lane = the slot's
 * location).
 */
export interface LaneSlot {
  laneId: string;
  dateKey: string;
  startMinutes: number;
  gameDurationInMinutes: number;
  /** Games this lane still takes on this date. */
  capacity: number;
}

export interface PlacementPlan {
  placed: GamePlacement[];
  unplaced: string[];
  lastDateKey: string | null;
}

const pad = (n: number) => String(n).padStart(2, '0');

export const parseDateKey = (key: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDateKey = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const addDays = (key: string, days: number): string => {
  const d = parseDateKey(key);
  if (!d) return key;
  d.setDate(d.getDate() + days);
  return formatDateKey(d);
};

export const dateKeyToAvailableDate = (key: string): string => {
  const [y, m, d] = key.split('-');
  return `${m}-${d}-${y}`;
};

/** Minutes since midnight for an "HH:mm" (or any parseable) time; NaN when unparseable. */
export const windowMinutes = (time: string): number => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((time || '').trim());
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  const parsed = parseTimeToMinutes(time);
  return Number.isFinite(parsed) ? parsed : NaN;
};

export const minutesToClock = (minutes: number): string => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/** Games that fit the window: floor((end - start) / duration). */
export const slotCapacity = (input: Pick<QuickAvailabilityInput, 'startTime' | 'endTime' | 'gameDurationInMinutes'>): number => {
  const start = windowMinutes(input.startTime);
  const end = windowMinutes(input.endTime);
  const dur = Number(input.gameDurationInMinutes);
  if (!Number.isFinite(start) || !Number.isFinite(end) || !(dur > 0) || end <= start) return 0;
  return Math.floor((end - start) / dur);
};

/** Every date in [dateFrom, dateTo] whose weekday is selected, ascending. Capped at 366 days. */
export const listSlotDates = (dateFrom: string, dateTo: string, daysOfWeek: number[]): string[] => {
  const from = parseDateKey(dateFrom);
  const to = parseDateKey(dateTo);
  if (!from || !to || to < from) return [];
  const wanted = new Set((daysOfWeek || []).map(Number));
  const out: string[] = [];
  const cursor = new Date(from);
  let guard = 0;
  while (cursor <= to && guard < 366) {
    if (wanted.has(cursor.getDay())) out.push(formatDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return out;
};

export const buildQuickSlots = (input: QuickAvailabilityInput): QuickSlot[] => {
  const capacity = slotCapacity(input);
  if (capacity === 0) return [];
  const start = minutesToClock(windowMinutes(input.startTime));
  const end = minutesToClock(windowMinutes(input.endTime));
  return listSlotDates(input.dateFrom, input.dateTo, input.daysOfWeek).map((dateKey) => ({
    dateKey,
    availableDate: dateKeyToAvailableDate(dateKey),
    startTime: start,
    endTime: end,
    gameDurationInMinutes: Number(input.gameDurationInMinutes),
    maxGames: capacity,
  }));
};

const byScheduleOrder = (a: PlaceableGame, b: PlaceableGame): number =>
  (a.dateKey || '').localeCompare(b.dateKey || '')
  || (Number(a.round) || 0) - (Number(b.round) || 0)
  || (Number(a.gameNumber) || 0) - (Number(b.gameNumber) || 0);

/**
 * Place time-less games into lane slots. A game keeps its date when some lane
 * still has room that date; otherwise it takes the next date on or after it
 * with room. On a date, lanes fill evenly (the least-filled lane first, ties
 * in lane order), so with 3 courts the first three games share the opening
 * time. Round order is preserved. Games with no date start at the first date.
 */
export const placeGamesIntoSlots = (games: PlaceableGame[], slots: LaneSlot[]): PlacementPlan => {
  const placed: GamePlacement[] = [];
  const unplaced: string[] = [];
  const usable = slots.filter((s) => s.capacity > 0 && Number.isFinite(s.startMinutes) && s.gameDurationInMinutes > 0 && !!parseDateKey(s.dateKey));
  if (!usable.length) return { placed, unplaced: games.map((g) => g.id), lastDateKey: null };
  const laneOrder = new Map<string, number>();
  usable.forEach((s) => { if (!laneOrder.has(s.laneId)) laneOrder.set(s.laneId, laneOrder.size); });
  const byDate = new Map<string, { slot: LaneSlot; filled: number }[]>();
  for (const slot of usable) {
    if (!byDate.has(slot.dateKey)) byDate.set(slot.dateKey, []);
    byDate.get(slot.dateKey)!.push({ slot, filled: 0 });
  }
  const dates = Array.from(byDate.keys()).sort();
  const roomOn = (dateKey: string) => (byDate.get(dateKey) || []).filter((l) => l.filled < l.slot.capacity);
  for (const game of [...games].sort(byScheduleOrder)) {
    let idx = dates.findIndex((d) => d >= (game.dateKey || ''));
    if (idx === -1) { unplaced.push(game.id); continue; }
    while (idx < dates.length && roomOn(dates[idx]).length === 0) idx += 1;
    if (idx >= dates.length) { unplaced.push(game.id); continue; }
    const dateKey = dates[idx];
    const lane = roomOn(dateKey).sort((x, y) => x.filled - y.filled || (laneOrder.get(x.slot.laneId)! - laneOrder.get(y.slot.laneId)!))[0];
    const startMinutes = lane.slot.startMinutes + lane.filled * lane.slot.gameDurationInMinutes;
    lane.filled += 1;
    placed.push({ id: game.id, dateKey, availableDate: dateKeyToAvailableDate(dateKey), startMinutes, time: formatMinutesForGameTime(startMinutes), laneId: lane.slot.laneId });
  }
  const lastDateKey = placed.reduce<string | null>((acc, p) => (acc === null || p.dateKey > acc ? p.dateKey : acc), null);
  return { placed, unplaced, lastDateKey };
};

/** The window as lane slots: `lanes` parallel courts, each with every date. */
export const laneSlotsFor = (input: QuickAvailabilityInput, lanes = 1): LaneSlot[] => {
  const quick = buildQuickSlots(input);
  const start = windowMinutes(input.startTime);
  const out: LaneSlot[] = [];
  for (let lane = 0; lane < Math.max(1, Math.floor(lanes) || 1); lane += 1) {
    for (const q of quick) {
      out.push({ laneId: String(lane), dateKey: q.dateKey, startMinutes: start, gameDurationInMinutes: q.gameDurationInMinutes, capacity: q.maxGames });
    }
  }
  return out;
};

/**
 * Place time-less games into the generated slots of one window. `lanes` > 1
 * (INT-0052: a venue with several courts) lets that many games share a start
 * time. Dates, spill and round order follow `placeGamesIntoSlots`.
 */
export const planGamePlacement = (games: PlaceableGame[], input: QuickAvailabilityInput, lanes = 1): PlacementPlan =>
  placeGamesIntoSlots(games, laneSlotsFor(input, lanes));

/** Wizard defaults: weekdays from the drafted games, else Saturday; range around them, else 8 weeks out. */
export const defaultQuickAvailability = (
  games: PlaceableGame[],
  opts: { today: string; gameDurationInMinutes?: number | null },
): QuickAvailabilityInput => {
  const dated = games.map((g) => g.dateKey).filter((k) => !!parseDateKey(k)).sort();
  const days = Array.from(new Set(dated.map((k) => (parseDateKey(k) as Date).getDay()))).sort((a, b) => a - b);
  return {
    daysOfWeek: days.length ? days : [6],
    startTime: '18:00',
    endTime: '21:00',
    gameDurationInMinutes: Number(opts.gameDurationInMinutes) > 0 ? Number(opts.gameDurationInMinutes) : 60,
    dateFrom: dated.length ? (dated[0] < opts.today ? opts.today : dated[0]) : opts.today,
    dateTo: dated.length ? addDays(dated[dated.length - 1], 7) : addDays(opts.today, 56),
  };
};
